import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { messagingAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { 
  House, 
  Users, 
  UserCircle,
  Calendar, 
  ClipboardText, 
  FileText, 
  ShieldCheck, 
  SignOut,
  List,
  Sparkle,
  ChatCircle,
  Buildings,
  CurrencyDollar,
  ChartLine,
  Receipt,
  Clock,
  Broadcast,
  Shield,
  Megaphone
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import ChangePasswordDialog from './ChangePasswordDialog';
import { CaregiverBottomNav, InstallAppPrompt } from './MobileCaregiver';
import { ConnectionStatusBar } from './OfflineSync';
import AssistantFab from './ai/AssistantFab';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    loadUnread();
    const interval = setInterval(loadUnread, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadUnread = async () => {
    try {
      const res = await messagingAPI.getUnreadCount();
      setUnreadCount(res.data.unread_count);
    } catch (e) { /* silent */ }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const navItems = [
    { path: '/offices', label: 'Offices', icon: Buildings, roles: ['super_admin', 'director'], perm: 'offices' },
    { path: '/roles-permissions', label: 'Roles & Permissions', icon: Shield, roles: ['super_admin', 'director'], perm: null },
    { path: '/dashboard', label: 'Dashboard', icon: House, roles: ['supervisor', 'admin', 'director', 'scheduler', 'caregiver', 'client', 'super_admin'], perm: 'dashboard' },
    { path: '/clients', label: 'Clients', icon: Users, roles: ['supervisor', 'admin', 'director', 'scheduler', 'caregiver', 'super_admin'], perm: 'clients' },
    { path: '/caregivers', label: 'Caregivers', icon: UserCircle, roles: ['supervisor', 'admin', 'director', 'scheduler', 'super_admin'], perm: 'caregivers' },
    { path: '/schedule', label: 'Schedule', icon: Calendar, roles: ['supervisor', 'admin', 'director', 'scheduler', 'caregiver', 'client', 'super_admin'], perm: 'schedule' },
    { path: '/open-shifts', label: 'Open Shifts', icon: Broadcast, roles: ['supervisor', 'admin', 'director', 'scheduler', 'super_admin'], perm: 'open-shifts' },
    { path: '/visit-logs', label: 'Care Notes', icon: FileText, roles: ['supervisor', 'admin', 'director', 'caregiver', 'client', 'super_admin'], perm: 'visit-logs' },
    { path: '/clock', label: 'Clock In/Out', icon: Clock, roles: ['caregiver', 'supervisor', 'admin', 'super_admin'], perm: 'clock' },
    { path: '/messages', label: 'Messages', icon: ChatCircle, roles: ['supervisor', 'admin', 'director', 'scheduler', 'caregiver', 'client', 'super_admin'], perm: 'messages', badge: unreadCount },
    { path: '/forms', label: 'Forms', icon: ClipboardText, roles: ['supervisor', 'admin', 'director', 'super_admin'], perm: 'forms' },
    { path: '/marketing', label: 'Marketing', icon: Megaphone, roles: ['supervisor', 'admin', 'director', 'super_admin'], perm: null },
    { path: '/ai-assistant', label: 'AI Assistant', icon: Sparkle, roles: ['supervisor', 'admin', 'director', 'super_admin'], perm: 'ai-assistant' },
    { path: '/audit-logs', label: 'Audit Logs', icon: List, roles: ['supervisor', 'admin', 'director', 'super_admin'], perm: 'audit-logs' },
  ];

  const financialItems = [
    { path: '/billing', label: 'Billing', icon: CurrencyDollar, perm: 'billing' },
    { path: '/payroll', label: 'Payroll', icon: CurrencyDollar, perm: 'payroll' },
    { path: '/invoices', label: 'Invoices', icon: Receipt, perm: 'invoices' },
    { path: '/reports', label: 'Reports', icon: ChartLine, perm: 'reports' },
    { path: '/holidays', label: 'Holiday Billing', icon: Calendar, perm: 'holidays' },
    { path: '/branding', label: 'Invoice Branding', icon: Buildings, perm: 'branding' },
  ];

  const perms = user?.permissions || {};
  const hasPerm = (key) => key ? (perms[key] !== false) : true; // default allow if undefined
  const financialRoles = ['supervisor', 'admin', 'director', 'super_admin', 'client'];
  const showFinancial = financialRoles.includes(user?.role) &&
    financialItems.some(i => hasPerm(i.perm));
  const isFinancialActive = ['/billing', '/payroll', '/invoices', '/reports', '/holidays', '/branding'].includes(location.pathname);

  const filteredNavItems = navItems.filter(item =>
    item.roles.includes(user?.role) && hasPerm(item.perm)
  );
  const filteredFinancialItems = financialItems.filter(i => hasPerm(i.perm));

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border/40" data-testid="app-header">
        <div className="container mx-auto px-4 md:px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" data-testid="header-logo-link">
              <img
                src="https://customer-assets.emergentagent.com/job_caregiver-portal-17/artifacts/qb31t681_CareGrid%20Logo.png"
                alt="CareGrid Logo"
                className="h-14 w-auto object-contain"
                data-testid="header-logo"
              />
            </Link>
          </div>

          <nav className="hidden md:flex items-center gap-1">
            {filteredNavItems.map(item => (
              <Link key={item.path} to={item.path}>
                <Button
                  variant={location.pathname === item.path || (item.path === '/clients' && location.pathname.startsWith('/clients/')) || (item.path === '/caregivers' && location.pathname.startsWith('/caregivers/')) ? 'secondary' : 'ghost'}
                  size="sm"
                  className="rounded-xl gap-2 relative"
                  data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                >
                  <item.icon weight="duotone" className="w-4 h-4" />
                  {item.label}
                  {item.badge > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full flex items-center justify-center">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </Button>
              </Link>
            ))}
            {showFinancial && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant={isFinancialActive ? 'secondary' : 'ghost'}
                    size="sm"
                    className="rounded-xl gap-2"
                    data-testid="nav-financial"
                  >
                    <CurrencyDollar weight="duotone" className="w-4 h-4" />
                    Financial
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-44 rounded-xl">
                  {filteredFinancialItems.map(fi => (
                    <DropdownMenuItem key={fi.path} asChild className="cursor-pointer">
                      <Link to={fi.path} className="flex items-center gap-2" data-testid={`nav-financial-${fi.label.toLowerCase()}`}>
                        <fi.icon weight="duotone" className="w-4 h-4" />
                        {fi.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </nav>

          <div className="flex items-center gap-3">
            {user?.office_name && (
              <Badge variant="outline" className="hidden sm:flex rounded-full gap-1 text-xs" data-testid="current-office-badge">
                <Buildings weight="duotone" className="w-3 h-3" />
                {user.office_name}
              </Badge>
            )}
            <span className="hipaa-badge hidden sm:flex">
              <ShieldCheck weight="duotone" className="w-3 h-3" />
              HIPAA
            </span>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="rounded-xl gap-2 p-2" data-testid="user-menu-trigger">
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {user ? getInitials(user.name) : '?'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block text-sm font-medium">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-xl">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span>{user?.name}</span>
                    <span className="text-xs font-normal text-muted-foreground capitalize">{user?.role?.replace('_', ' ')}</span>
                    {user?.office_name && <span className="text-xs font-normal text-muted-foreground">{user.office_name}</span>}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setIsChangePasswordOpen(true)} className="cursor-pointer" data-testid="change-password-btn">
                  <SignOut className="w-4 h-4 mr-2 rotate-180" />
                  Change Password
                </DropdownMenuItem>
                {user?.role === 'super_admin' && (
                  <DropdownMenuItem onClick={() => navigate('/offices')} className="cursor-pointer" data-testid="back-to-offices-btn">
                    <Buildings className="w-4 h-4 mr-2" />
                    Manage Offices
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer" data-testid="logout-button">
                  <SignOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden border-t border-border/40 overflow-x-auto">
          <nav className="flex items-center gap-1 px-4 py-2">
            {filteredNavItems.map(item => (
              <Link key={item.path} to={item.path}>
                <Button
                  variant={location.pathname === item.path ? 'secondary' : 'ghost'}
                  size="sm"
                  className="rounded-xl gap-1 whitespace-nowrap"
                >
                  <item.icon weight="duotone" className="w-4 h-4" />
                  <span className="text-xs">{item.label}</span>
                </Button>
              </Link>
            ))}
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className={`container mx-auto p-4 md:p-6 lg:p-8 ${user?.role === 'caregiver' ? 'pb-24 md:pb-8' : ''}`}>
        {children}
      </main>

      {/* Floating AI assistant for office staff */}
      {['supervisor', 'admin', 'director', 'super_admin'].includes(user?.role) && location.pathname !== '/ai-assistant' && <AssistantFab />}

      {/* Mobile bottom nav for caregivers */}
      <CaregiverBottomNav user={user} unreadCount={unreadCount} />
      {/* Offline / pending-sync status bar */}
      <ConnectionStatusBar user={user} />
      {/* PWA install prompt */}
      <InstallAppPrompt />

      {/* Change Password Dialog — opens from user menu OR forced when must_change_password */}
      <ChangePasswordDialog
        open={isChangePasswordOpen || !!user?.must_change_password}
        onOpenChange={setIsChangePasswordOpen}
        forced={!!user?.must_change_password}
      />
    </div>
  );
};

export default Layout;
